import { useState } from "react"
import { useTranslation } from "react-i18next"

import { InviteCopyButton } from "@/components/invites/invite-copy-button"
import { InviteOverflowMenu } from "@/components/invites/invite-overflow-menu"
import { InviteQrButton } from "@/components/invites/invite-qr-button"
import { InviteShareButton } from "@/components/invites/invite-share-button"
import { InviteStatePill } from "@/components/invites/invite-state-pill"
import { InvitesPanelEmpty } from "@/components/invites/invites-panel-empty"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useIsMobile } from "@/hooks/use-mobile"
import { useOnline } from "@/hooks/use-online"
import { cn } from "@/lib/utils"
import type { CandidateActions } from "@/types/candidates"
import type { InviteActions, InviteSummary } from "@/types/invites"

/**
 * OKS-21 — Brand-side Invites panel rendered inside Candidate
 * Detail (OKS-18 page).
 *
 * **State branches** (decided by props):
 *   1. `invite === null` AND `invite_actions.generate_url !== null` →
 *      `<InvitesPanelEmpty mode="approved">` w/ Generate CTA.
 *   2. `invite === null` AND `invite_actions.generate_url === null` →
 *      `mode="not_approved"`. OKS-673: this is now the green-flow
 *      driver — it renders the inline primary advance action
 *      (`Mark as reviewed` when `actions.mark_reviewed_url`, `Approve`
 *      when `actions.approve_url`), or guidance copy when neither
 *      (rejected / blocklisted / viewer not allowed).
 *   3. `invite !== null` → populated card with status pill, URL,
 *      meta lines, Copy/Share row, overflow menu.
 *
 * **OKS-673 — `actions` threaded in.** The transition surface
 * (`CandidateActions`) is passed through so the pre-approval empty
 * state can render the relocated green-flow buttons off the SAME
 * `*_url` nullability the action bar used. FE never re-derives from
 * `candidate.status`; a null URL means "not available for this
 * viewer/brand" (same gate).
 *
 * **Mobile vs desktop primary action.** UX Spec §2.1: Copy is
 * primary on desktop (icon + label, default variant); Share is
 * primary on mobile (full-width sticky CTA at the bottom of the
 * card). We re-order the action row + flip variant by `useIsMobile`.
 *
 * **No flash mounting here.** `useFlash` is mounted once by
 * `PersistentLayout`; controller flash[:success] from generate /
 * regenerate / revoke / copy comes through automatically.
 *
 * **Action visibility = single discriminator.** `null` URLs in
 * `invite_actions` are the contract (mirrors OKS-18/19). FE never
 * computes "should I show this button" from the candidate status —
 * backend gates via policy + state guard, FE just renders what it's
 * told.
 */

interface InvitesPanelProps {
  invite: InviteSummary | null
  invite_actions: InviteActions
  /**
   * OKS-673 — candidate transition surface. Consumed by the
   * pre-approval empty state to render the inline green-flow advance
   * action (`mark_reviewed_url` / `approve_url`). Same nullability
   * gate as the action bar — no re-derivation from candidate status.
   */
  actions: CandidateActions
  candidate_handle: string
  className?: string
}

export function InvitesPanel({
  invite,
  invite_actions,
  actions,
  candidate_handle,
  className,
}: InvitesPanelProps) {
  const { t } = useTranslation()

  return (
    <Card
      data-testid="invites-panel"
      className={cn("flex flex-col", className)}
    >
      <CardHeader>
        <CardTitle>{t("invites.panel.title")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {invite === null ? (
          <InvitesPanelEmpty
            // Per BE contract: `generate_url` is non-null iff candidate
            // is `approved`/`invited` AND policy allows. Use that as
            // the single discriminator rather than re-deriving from
            // candidate_status.
            mode={invite_actions.generate_url ? "approved" : "not_approved"}
            generateUrl={invite_actions.generate_url}
            // OKS-673 — inline green-flow advance URLs (pre-approval).
            markReviewedUrl={actions.mark_reviewed_url}
            approveUrl={actions.approve_url}
            handle={candidate_handle}
          />
        ) : (
          <ActiveInvite invite={invite} invite_actions={invite_actions} />
        )}
      </CardContent>
    </Card>
  )
}

interface ActiveInviteProps {
  invite: InviteSummary
  invite_actions: InviteActions
}

function ActiveInvite({ invite, invite_actions }: ActiveInviteProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const online = useOnline()
  const { currentBrand } = useCurrentContext()
  const brandName = currentBrand?.name ?? ""

  return (
    <div data-testid="invites-panel-active" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <InviteStatePill state={invite.state} />
      </div>
      {!online && (
        <p
          role="status"
          className="text-muted-foreground text-sm"
          data-testid="invites-panel-offline"
        >
          {t("invites.panel.offline")}
        </p>
      )}

      <div
        data-testid="invites-panel-url"
        className="bg-muted/40 text-muted-foreground rounded-md border px-3 py-2 font-mono text-sm break-all"
      >
        {invite.url}
      </div>

      <Meta invite={invite} />

      {/* Mobile: Share primary (full-width), Copy secondary outline,
          overflow inline. Desktop: Copy primary (default), Share
          outline, overflow inline. */}
      {isMobile ? (
        <div className="flex flex-col gap-2 pt-2">
          <InviteShareButton
            url={invite.url}
            brandName={brandName}
            variant="default"
            className="w-full"
          />
          <div className="flex items-center gap-2">
            <InviteCopyButton
              url={invite.url}
              variant="outline"
              className="flex-1"
            />
            <InviteQrButton
              url={invite.url}
              variant="outline"
              className="flex-1"
            />
            <InviteOverflowMenu
              regenerateUrl={invite_actions.generate_url}
              revokeUrl={invite_actions.revoke_url}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <InviteCopyButton url={invite.url} variant="default" />
          <InviteShareButton
            url={invite.url}
            brandName={brandName}
            variant="outline"
          />
          <InviteQrButton url={invite.url} variant="outline" />
          <InviteOverflowMenu
            regenerateUrl={invite_actions.generate_url}
            revokeUrl={invite_actions.revoke_url}
            className="ml-auto"
          />
        </div>
      )}
    </div>
  )
}

interface MetaProps {
  invite: InviteSummary
}

function Meta({ invite }: MetaProps) {
  const { t } = useTranslation()

  // `Date.now()` is impure-during-render per react/purity lint.
  // Snapshot via lazy-initial useState — captured once at first
  // mount + held stable for all subsequent re-renders. The page gets
  // reloaded by Inertia whenever the server-side state changes
  // (regenerate / revoke / settings update), so any stale "days
  // left" math gets refreshed by the next visit; we don't need a
  // wall-clock ticker here.
  const expiresDate = new Date(invite.expires_at)
  const [now] = useState<number>(() => Date.now())

  const daysLeft = Math.max(
    0,
    Math.ceil((expiresDate.getTime() - now) / (1000 * 60 * 60 * 24)),
  )
  const isExpired = expiresDate.getTime() <= now
  const dateString = expiresDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  const generatedDateString = new Date(invite.generated_at).toLocaleDateString(
    undefined,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
  )
  const firstClickDateString = invite.first_clicked_at
    ? new Date(invite.first_clicked_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null
  const lastClickDateString = invite.last_clicked_at
    ? new Date(invite.last_clicked_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null

  return (
    <dl className="text-muted-foreground grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
      <div
        data-testid="invites-panel-expires"
        className="flex flex-col gap-0.5"
      >
        <dt className="text-xs font-medium tracking-wide uppercase">
          {t("invites.panel.meta.expires", { date: dateString })}
        </dt>
        <dd className="text-foreground text-sm">
          {isExpired
            ? t("invites.panel.meta.expired")
            : t("invites.panel.meta.expires_in_days", { count: daysLeft })}
        </dd>
        <dd className="text-muted-foreground text-xs">
          {t("invites.panel.meta.generated_at", { date: generatedDateString })}
        </dd>
      </div>
      <div
        data-testid="invites-panel-click-stats"
        className="flex flex-col gap-0.5"
      >
        <dt className="text-xs font-medium tracking-wide uppercase">
          {clickCountCopy(t, invite.click_count)}
        </dt>
        {firstClickDateString && (
          <dd className="text-foreground text-sm">
            {t("invites.panel.meta.first_clicked", {
              date: firstClickDateString,
            })}
          </dd>
        )}
        {lastClickDateString && (
          <dd className="text-muted-foreground text-xs">
            {t("invites.panel.meta.last_clicked", {
              date: lastClickDateString,
            })}
          </dd>
        )}
      </div>
    </dl>
  )
}

/**
 * Resolve the click-count label via literal `t()` arguments — keeps
 * the i18next-health scanner happy + matches the
 * `meta.click_count_{zero|one|other}` keys defined in the EN/FR YAML.
 */
function clickCountCopy(
  t: ReturnType<typeof useTranslation>["t"],
  count: number,
): string {
  if (count === 0) return t("invites.panel.meta.click_count_zero")
  if (count === 1) return t("invites.panel.meta.click_count_one")
  return t("invites.panel.meta.click_count_other", { count })
}
